import type { Product, Promotion, ReviewItem, SearchParams } from "@/types";

import { apiGet } from "@/services/api";

/**
 * Browser-side replacements for the server-only helpers in `lib/products.ts`,
 * `lib/categories.ts`, `lib/promotions.ts` and `lib/reviews.ts`.
 *
 * Each of those used to run Prisma directly inside a server component. They now
 * call the equivalent NestJS endpoint, so the payloads — and therefore every
 * component that consumes them — are unchanged.
 */
/** Matches `lib/products.ts`'s `ProductPage`, which the API also returns. */
export type ProductPage = {
  items: Product[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

export type { Product, Promotion, ReviewItem, SearchParams };

type ProductRow = { product: Product; related: Product[]; alsoBought: Product[]; similar: Product[]; reviews: ReviewItem[]; bundle: Product[] };

/** `GET /api/products/sections/:section?limit=` */
export async function getSectionProducts(section: string, limit = 6): Promise<Product[]> {
  const data = await apiGet<{ items: Product[] }>(`/api/products/sections/${section}?limit=${limit}`);
  return data?.items ?? [];
}

export const getTrendingProducts = (limit = 6) => getSectionProducts("trending", limit);
export const getBestSellers = (limit = 6) => getSectionProducts("best-sellers", limit);
export const getNewArrivals = (limit = 6) => getSectionProducts("new-arrivals", limit);
export const getFeaturedProducts = () => getSectionProducts("featured", 12);
export const getTodaysDeals = (limit = 10) => getSectionProducts("todays-deals", limit);
export const getPrimeDeals = (limit = 8) => getSectionProducts("prime-deals", limit);
export const getSponsoredProducts = (limit = 4) => getSectionProducts("sponsored", limit);
export const getRecommendedProducts = (limit = 6) => getSectionProducts("recommended", limit);

export async function getProductsByCategories(categories: string[], limit = 5): Promise<Product[]> {
  const data = await apiGet<{ items: Product[] }>(
    `/api/products?category=${encodeURIComponent(categories.join(","))}&perPage=${limit}`,
  );
  return data?.items ?? [];
}

/** `GET /api/products?<SearchParams>&perPage=` */
export async function queryProducts(params: SearchParams, perPage = 8): Promise<ProductPage> {
  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, String(value));
  }
  query.set("perPage", String(perPage));

  const data = await apiGet<ProductPage>(`/api/products?${query.toString()}`);
  return data ?? { items: [], total: 0, page: 1, perPage, totalPages: 1 };
}

/** `GET /api/categories` → `{ categories: string[] }`. */
export async function getCategories(): Promise<string[]> {
  const data = await apiGet<{ categories?: string[] }>("/api/categories");
  return data?.categories ?? [];
}

/** `GET /api/promotions` → `{ promotions: Promotion[] }`. */
export async function getActivePromotions(): Promise<Promotion[]> {
  const data = await apiGet<{ promotions?: Promotion[] }>("/api/promotions");
  return data?.promotions ?? [];
}

/** `GET /api/products/by-slug/:slug` — product plus every carousel on the page. */
export async function getProductDetail(slug: string): Promise<ProductRow | null> {
  return apiGet<ProductRow>(`/api/products/by-slug/${encodeURIComponent(slug)}`);
}

/** `GET /api/products/slugs` is `/api/products?slugs=` — used by compare/quick views. */
export async function getProductsBySlugs(slugs: string[]): Promise<Product[]> {
  if (slugs.length === 0) return [];
  const data = await apiGet<{ items: Product[] }>(`/api/products?slugs=${encodeURIComponent(slugs.join(","))}`);
  return data?.items ?? [];
}

export type HomePayload = {
  categories: string[];
  promotions: Promotion[];
  todaysDeals: Product[];
  featured: Product[];
  trending: Product[];
  bestSellers: Product[];
  newArrivals: Product[];
  recommended: Product[];
  primeDeals: Product[];
  sponsored: Product[];
  electronics: Product[];
  home: Product[];
};

export const EMPTY_HOME: HomePayload = {
  categories: [],
  promotions: [],
  todaysDeals: [],
  featured: [],
  trending: [],
  bestSellers: [],
  newArrivals: [],
  recommended: [],
  primeDeals: [],
  sponsored: [],
  electronics: [],
  home: [],
};

/**
 * `GET /api/home` replaces the twelve parallel Prisma queries the homepage
 * server component used to run, so this is one round trip instead of twelve.
 */
export async function getHome(): Promise<HomePayload> {
  return (await apiGet<HomePayload>("/api/home")) ?? EMPTY_HOME;
}